/**
 * waitForModelReady — batch SSE model processing progress waiter.
 *
 * Every call registers the model id with a single, shared SSE stream at
 * `GET /v2/ecommerce/models/progress` rather than opening one stream per model.
 * Dropping N files therefore uses ONE server connection instead of N, which is
 * what kept the server's request threads from being pinned by concurrent waits.
 *
 * The public signature is unchanged: callers still `await waitForModelReady(id,
 * token)` and branch on the returned terminal status. All multiplexing —
 * reference counting, reconnection, and dispatch-by-id — is internal.
 *
 * Uses fetch + ReadableStream instead of native EventSource so we can send the
 * Authorization header (EventSource does not support custom headers).
 */

import { API_URL } from "util/network/common";
import { apiGetModel } from "util/network/Models";

export type ModelTerminalStatus = "Good" | "Bad" | "Cancelled";

/**
 * Statuses at which the server stops working a model. "Init" is included
 * because a model that never needed processing reports it directly.
 * Non-terminal (still in flight): "Pending", "Processing".
 */
const TERMINAL_STATES: Set<string> = new Set(["Init", "Good", "Bad"]);
const DEFAULT_RETRY_DELAY_MS = 3000;
// Genuine client-side backstop. If a model finishes before the shared stream
// opens it is never re-reported, so its waiter would otherwise hang. Cap total
// wall-clock at ~2x the server's 10-min window so a stalled model can't hang
// the UI; the waiter resolves "Bad" and its removal lets the stream close.
const MAX_WAIT_MS = 20 * 60 * 1000;

interface Waiter {
  resolve: (status: ModelTerminalStatus) => void;
  settle: (status: ModelTerminalStatus) => void;
}

interface StreamMeta {
  retryDelayMs: number;
  lastEventId: string | null;
}

// Registered waiters, keyed by model id. Multiple callers may await the same id.
const waiters = new Map<number, Set<Waiter>>();

// The single shared stream. `streamActive` guards against opening a second one;
// `streamAbort` lets us tear the current connection down when no waiters remain.
let streamActive = false;
let streamAbort: AbortController | null = null;
// Credentials/base URL for the stream, refreshed by the most recent registrant.
let streamToken = "";
let streamApiUrl = "";

/**
 * Waits for a model to reach a terminal processing state by subscribing to the
 * shared batch progress stream.
 *
 * @returns Promise resolving to `'Good'` on success, `'Bad'` on failure, or
 *          `'Cancelled'` if the caller aborted via `signal` (neutral — not a
 *          processing failure, so callers should not surface it as an error).
 */
export function waitForModelReady(
  modelId: number,
  token: string,
  apiUrl: string = API_URL ?? "",
  signal?: AbortSignal
): Promise<ModelTerminalStatus> {
  if (signal?.aborted) return Promise.resolve("Cancelled");

  // Newest registrant wins for the stream's credentials — within a session
  // these are identical, but a re-login mid-batch should use the fresh token.
  streamToken = token;
  streamApiUrl = apiUrl;

  return new Promise<ModelTerminalStatus>((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const onAbort = () => waiter.settle("Cancelled");

    const waiter: Waiter = {
      resolve,
      settle: (status) => {
        // Deregister exactly once, then clean up this waiter's own resources.
        const set = waiters.get(modelId);
        if (set && set.delete(waiter) && set.size === 0) {
          waiters.delete(modelId);
        }
        clearTimeout(timeoutId);
        signal?.removeEventListener("abort", onAbort);
        resolve(status);
        stopStreamIfIdle();
      },
    };

    let set = waiters.get(modelId);
    if (!set) {
      set = new Set();
      waiters.set(modelId, set);
    }
    set.add(waiter);

    timeoutId = setTimeout(() => waiter.settle("Bad"), MAX_WAIT_MS);
    signal?.addEventListener("abort", onAbort);

    ensureStream();
  });
}

/** Resolve every waiter registered for a model that reached a terminal state. */
function dispatchTerminal(modelId: number, status: string): void {
  const set = waiters.get(modelId);
  if (!set) return;
  const result: ModelTerminalStatus = status === "Bad" ? "Bad" : "Good";
  // Copy first: settle() mutates the set as it deregisters each waiter.
  for (const waiter of [...set]) {
    waiter.settle(result);
  }
}

/** Resolve every still-registered waiter with the same terminal status. */
function settleAllRemaining(status: ModelTerminalStatus): void {
  for (const set of [...waiters.values()]) {
    for (const waiter of [...set]) {
      waiter.settle(status);
    }
  }
}

/**
 * Resolves waiters still registered when the stream closes with `done`. Such a
 * model was terminal before the stream opened (so it was never reported in
 * `models[]`), or was registered as this stream was closing. Rather than assume
 * failure, fetch each model's authoritative status once; only give up as "Bad"
 * if that lookup fails or still reports a non-terminal state.
 */
async function resolveStragglers(): Promise<void> {
  await Promise.all(
    [...waiters.keys()].map(async (id) => {
      try {
        const model = await apiGetModel(streamToken, id);
        if (TERMINAL_STATES.has(model.status)) {
          dispatchTerminal(id, model.status);
        }
      } catch {
        // Leave unresolved — the blanket settle below stops it hanging.
      }
    })
  );
  settleAllRemaining("Bad");
}

/** Start the shared stream loop if it is not already running. */
function ensureStream(): void {
  if (!streamActive) {
    void runStream();
  }
}

/** Tear down the shared connection once the last waiter has resolved. */
function stopStreamIfIdle(): void {
  if (waiters.size === 0 && streamAbort) {
    streamAbort.abort();
  }
}

/**
 * Runs the shared batch stream until no waiters remain. Reconnects on drop with
 * the advertised retry delay, sending Last-Event-ID so the server can
 * fast-forward. Stops for good on the server's `"done": true` sentinel, so it
 * never reconnects into the done/empty stream the server keeps answering with.
 */
async function runStream(): Promise<void> {
  streamActive = true;
  const meta: StreamMeta = {
    retryDelayMs: DEFAULT_RETRY_DELAY_MS,
    lastEventId: null,
  };

  try {
    while (waiters.size > 0) {
      const abort = new AbortController();
      streamAbort = abort;

      const url = `${streamApiUrl}/v2/ecommerce/models/progress`;
      const headers: Record<string, string> = {
        Authorization: `Bearer ${streamToken}`,
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
      };
      if (meta.lastEventId !== null) {
        headers["Last-Event-ID"] = meta.lastEventId;
      }

      let response: Response;
      try {
        response = await fetch(url, { headers, signal: abort.signal });
      } catch {
        if (abort.signal.aborted) continue; // stop requested — re-check loop
        await delay(meta.retryDelayMs);
        continue;
      }

      if (!response.ok || !response.body) {
        // Transient server error (e.g. a restart). Back off and retry; the
        // per-waiter MAX_WAIT backstop bounds this if it never recovers.
        await delay(meta.retryDelayMs);
        continue;
      }

      const sawDone = await consumeBatchStream(response.body, meta);

      if (sawDone) {
        // Deterministic close signal. Resolve any waiter still registered here
        // from its authoritative status rather than reconnecting into the
        // done/empty loop the server would keep answering with.
        await resolveStragglers();
        break;
      }

      // Stream ended without the sentinel: server-closed (all terminal ->
      // waiters empty -> exit) or a mid-flight drop (waiters remain -> back off
      // and reconnect with Last-Event-ID).
      if (waiters.size > 0 && !abort.signal.aborted) {
        await delay(meta.retryDelayMs);
      }
    }
  } finally {
    streamActive = false;
    streamAbort = null;
  }
}

/**
 * Reads the batch SSE stream, dispatching each event's terminal models to their
 * waiters.
 *
 * @returns `true` if the stream ended on the server's `"done": true` sentinel
 *          (deterministic close — do not reconnect), or `false` if it ended by
 *          reader close, network drop, or abort (caller decides whether to
 *          retry).
 */
async function consumeBatchStream(
  body: ReadableStream<Uint8Array>,
  meta: StreamMeta
): Promise<boolean> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await reader.read();
      } catch {
        return false; // network read error or abort — caller decides on retry
      }

      const { done, value } = readResult;
      if (done) return false;

      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by blank lines (\n\n).
      const messages = buffer.split("\n\n");
      buffer = messages.pop() ?? ""; // keep the trailing partial message

      for (const message of messages) {
        if (parseBatchMessage(message, meta)) {
          return true; // "done": true — server is closing, stop reading
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Parses one SSE message block. Updates `meta` for `retry:`/`id:` lines and
 * dispatches terminal models from the `data:` payload
 * (`{ "models": [{ "id": number, "status": string }], "done"?: boolean }`).
 *
 * @returns `true` if the payload carried the `"done": true` close sentinel. The
 *          event's models are dispatched first, so any final-event terminal
 *          states resolve their waiters before the caller acts on `done`.
 */
function parseBatchMessage(message: string, meta: StreamMeta): boolean {
  let done = false;
  for (const line of message.split("\n")) {
    if (line.startsWith("retry:")) {
      const ms = parseInt(line.slice(6).trim(), 10);
      if (!isNaN(ms)) meta.retryDelayMs = ms;
    } else if (line.startsWith("id:")) {
      meta.lastEventId = line.slice(3).trim();
    } else if (line.startsWith("data:")) {
      const raw = line.slice(5).trim();
      let payload: {
        models?: { id?: number; status?: string }[];
        done?: boolean;
      };
      try {
        payload = JSON.parse(raw);
      } catch {
        continue; // malformed JSON — ignore this event
      }
      for (const model of payload.models ?? []) {
        if (
          typeof model.id === "number" &&
          model.status !== undefined &&
          TERMINAL_STATES.has(model.status)
        ) {
          dispatchTerminal(model.id, model.status);
        }
      }
      if (payload.done === true) done = true;
    }
  }
  return done;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
